/**
 * Enhanced Import Logging Subsystem
 * 
 * Consolidates warnings, hints, and errors with selective display logic.
 * Provides security-compliant logging that masks sensitive URLs.
 */

import dayjs from 'dayjs'
import type { ImportStats, ImportError, ImportWarning, ImportHint } from './types'

/**
 * Details of individual attachment upload failures for enhanced error reporting
 */
export type AttachmentFailure = {
  /** File path where the attachment failure occurred */
  notePath: string
  /** Name of the attachment file that failed */
  filename: string
  /** Error message describing the failure */
  error: string
}

/**
 * Complete import log with all types of feedback
 */
export type ImportLog = {
  /** List of files that were attempted to import */
  filesAttempted: string[]
  /** Import statistics summary */
  stats: ImportStats
  /** Array of errors that occurred during import */
  errors: ImportError[]
  /** Array of warnings for missing attachment references */
  warnings: ImportWarning[]
  /** Array of hints for unused attachment files */
  hints: ImportHint[]
  /** Array of attachment upload failures for enhanced error reporting */
  attachmentsFailed: AttachmentFailure[]
}

/**
 * Masks sensitive URLs in log messages for security compliance
 * Replaces signed URLs with a redacted placeholder
 * 
 * @param message - The message that may contain sensitive URLs
 * @returns Message with URLs masked as <signed-url:redacted>
 */
export function maskSensitiveUrls(message: string): string {
  // Pattern to match various URL formats that might be signed URLs
  const urlPatterns = [
    // Supabase signed URLs
    /https:\/\/[^/]+\.supabase\.co\/storage\/v1\/object\/sign\/\S+/g,
    // Generic signed URL patterns with tokens/signatures
    /https?:\/\/\S*[&?](token|signature|sign|auth)=[^\s&]*/g,
    // CloudKit asset URLs
    /asset:\/\/\S+/g,
  ]

  let maskedMessage = message
  for (const pattern of urlPatterns) {
    maskedMessage = maskedMessage.replace(pattern, '<signed-url:redacted>')
  }

  return maskedMessage
}

/**
 * Determines if the import log should be displayed based on selective display logic
 * Shows log when there are errors, warnings, or attachment failures (hints alone don't trigger display)
 *
 * @param log - The import log to evaluate
 * @returns True if the log should be displayed to the user
 */
export function shouldDisplayLog(log: ImportLog): boolean {
  return log.errors.length > 0 || log.warnings.length > 0 || log.attachmentsFailed.length > 0
}

/**
 * Renders the import log as markdown content
 * Creates a structured note with sections for different types of feedback
 * 
 * @param log - The import log to render
 * @param timestamp - Optional timestamp (defaults to current time)
 * @returns Markdown content for the import log note
 */
export function renderImportLogMarkdown(log: ImportLog, timestamp?: string): string {
  const logTimestamp = timestamp ?? dayjs().format('YYYY-MM-DD HH:mm:ss')
  
  const contentLines = [
    `# Import Log - ${logTimestamp}`,
    ``,
    `## Files Selected`,
    ...log.filesAttempted.map((file) => `- \`${file}\``),
    ``,
    `## Summary`,
    `- **Created**: ${String(log.stats.created)} notes/folders`,
    `- **Duplicated**: ${String(log.stats.duplicated)} items`,
    `- **Overwritten**: ${String(log.stats.overwritten)} items`,
    `- **Skipped**: ${String(log.stats.skipped)} items`,
    `- **Errors**: ${String(log.stats.errors)} failed items`,
    ``,
  ]

  // Add attachment failures section for upload errors
  if (log.attachmentsFailed.length > 0) {
    contentLines.push(
      `## ❌ Attachment Upload Failures`,
      `The following attachments failed to upload during import:`,
      ``,
      ...log.attachmentsFailed.map((failure) => {
        const maskedError = maskSensitiveUrls(failure.error)
        return `- \`${failure.notePath}\` — **${failure.filename}**: ${maskedError.replaceAll('\n', ' ')}`
      }),
      ``
    )
  }

  // Add warnings section for missing attachment references
  if (log.warnings.length > 0) {
    contentLines.push(
      `## ⚠️ Missing Attachment References`,
      `The following attachments are referenced in notes but were not found in the import files:`,
      ``,
      ...log.warnings.map((warning) => {
        const maskedMessage = maskSensitiveUrls(warning.message)
        return `- \`${warning.path}\` — ${maskedMessage.replaceAll('\n', ' ')}`
      }),
      ``
    )
  }

  // Add hints section for unused attachment files
  if (log.hints.length > 0) {
    contentLines.push(
      `## 💡 Unused Attachment Files`,
      `The following files were imported but are not referenced in any notes:`,
      ``,
      ...log.hints.map((hint) => {
        const maskedMessage = maskSensitiveUrls(hint.message)
        return `- \`${hint.path}\` — ${maskedMessage.replaceAll('\n', ' ')}`
      }),
      ``
    )
  }

  // Add error details section
  contentLines.push(
    `## ❌ Error Details`,
    ...(log.errors.length === 0
      ? ['No errors occurred during import 🎉']
      : [
          `The following files could not be imported:`,
          ``,
          ...log.errors.map((error) => {
            const maskedMessage = maskSensitiveUrls(error.message)
            return `- \`${error.path}\` — ${maskedMessage.replaceAll('\n', ' ')}`
          })
        ]),
    ``,
    `---`,
    `*Generated automatically by import process*`
  )

  return contentLines.join('\n')
}

/**
 * Creates a filename for the import log note
 * 
 * @param timestamp - Optional timestamp (defaults to current time)
 * @returns Filename for the import log note
 */
export function createImportLogFilename(timestamp?: string): string {
  const logTimestamp = timestamp ?? dayjs().format('YYYY-MM-DD HH:mm:ss')
  return `Import Log - ${logTimestamp}`
}

/**
 * Validates that an import log contains the required fields
 * 
 * @param log - The import log to validate
 * @returns True if the log is valid
 */
export function validateImportLog(log: ImportLog): boolean {
  return (
    Array.isArray(log.filesAttempted) &&
    typeof log.stats === 'object' &&
    Array.isArray(log.errors) &&
    Array.isArray(log.warnings) &&
    Array.isArray(log.hints) &&
    Array.isArray(log.attachmentsFailed)
  )
}

/**
 * Creates a summary message for notifications
 * Provides a compact overview of the import results
 * 
 * @param log - The import log to summarize
 * @returns Human-readable summary string
 */
export function createImportSummary(log: ImportLog): string {
  const parts: string[] = []

  if (log.stats.created > 0) {
    parts.push(`${String(log.stats.created)} imported`)
  }

  if (log.stats.errors > 0) {
    parts.push(`${String(log.stats.errors)} failed`)
  }

  if (log.attachmentsFailed.length > 0) {
    parts.push(`${String(log.attachmentsFailed.length)} attachment failures`)
  }

  if (log.warnings.length > 0) {
    parts.push(`${String(log.warnings.length)} warnings`)
  }

  if (log.hints.length > 0) {
    parts.push(`${String(log.hints.length)} hints`)
  }

  return parts.length > 0 ? parts.join(', ') : 'No issues'
}
